import { createFileRoute } from '@tanstack/react-router';
import { useServerFn, createServerFn } from '@tanstack/react-start';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { requireSupabaseAuth } from '@/integrations/supabase/auth-middleware';
import { supabaseAdmin } from '@/integrations/supabase/client.server';
import { usePermissions } from '@/hooks/use-permissions';
import { BackButton } from '@/components/BackButton';
import {
  PermissionLock,
  PermissionLockLoading,
} from '@/components/admin/PermissionLock';

const listSubs = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { search?: string; limit?: number }) =>
    z.object({ search: z.string().max(200).optional(), limit: z.number().min(1).max(200).default(100) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { data: roleRows } = await supabaseAdmin
      .from('user_roles')
      .select('role')
      .eq('user_id', context.userId)
      .in('role', ['super_admin', 'admin', 'finance', 'support']);
    if (!roleRows || roleRows.length === 0) throw new Error('forbidden');
    let q = supabaseAdmin
      .from('subscriptions')
      .select('user_id, provider, platform_product_id, tier, status, environment, expires_at, grace_period_end, auto_renew, refunded_at, last_verified_at, original_transaction_id, updated_at')
      .order('updated_at', { ascending: false })
      .limit(data.limit);
    if (data.search) q = q.ilike('original_transaction_id', `%${data.search}%`);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return { rows: rows ?? [] };
  });

export const Route = createFileRoute('/_authenticated/app/admin/subscriptions')({
  component: AdminSubscriptions,
  head: () => ({ meta: [{ title: 'Subscriptions — Admin' }] }),
});

function AdminSubscriptions() {
  const perms = usePermissions();
  const allowed = perms.canViewUsers || perms.canManageBilling;
  const fn = useServerFn(listSubs);
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-subs'],
    queryFn: () => fn({ data: { limit: 100 } }),
    enabled: !perms.loading && allowed,
  });

  if (perms.loading) return <PermissionLockLoading />;
  if (!allowed) {
    return (
      <PermissionLock
        titleEn="Subscriptions access required"
        titleAr="صلاحية الاشتراكات مطلوبة"
        messageEn="Subscription data is restricted to support, finance, and super admins."
        messageAr="بيانات الاشتراكات متاحة لفريق الدعم والمالية والمسؤول العام فقط."
      />
    );
  }

  return (
    <main className="container mx-auto py-8">
      <div className="mb-3"><BackButton /></div>
      <h1 className="text-2xl font-semibold mb-4">Subscriptions</h1>
      {isLoading && <p className="text-muted-foreground">Loading…</p>}
      {error && <p className="text-destructive">{(error as Error).message}</p>}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left">
            <tr>
              <th className="p-2">Provider</th>
              <th className="p-2">Tier</th>
              <th className="p-2">Status</th>
              <th className="p-2">Env</th>
              <th className="p-2">Product</th>
              <th className="p-2">Expires</th>
              <th className="p-2">Auto-renew</th>
              <th className="p-2">Verified</th>
              <th className="p-2">Original Txn</th>
            </tr>
          </thead>
          <tbody>
            {data?.rows.map((r) => (
              <tr key={`${r.user_id}-${r.original_transaction_id ?? ''}`} className="border-t">
                <td className="p-2">{r.provider ?? '—'}</td>
                <td className="p-2">{r.tier}</td>
                <td className="p-2">{r.status}</td>
                <td className="p-2">{r.environment}</td>
                <td className="p-2 max-w-[200px] truncate">{r.platform_product_id ?? '—'}</td>
                <td className="p-2">{r.expires_at ? new Date(r.expires_at).toLocaleString() : '—'}</td>
                <td className="p-2">{r.auto_renew ? 'Yes' : 'No'}</td>
                <td className="p-2">{r.last_verified_at ? new Date(r.last_verified_at).toLocaleString() : '—'}</td>
                <td className="p-2 font-mono text-xs max-w-[180px] truncate">{r.original_transaction_id ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
}
